///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Point-in-alert queries against the local weather_alerts table, used by
/// the placemark callout's live alert roll-up ("2 of 14 locations under
/// Severe Thunderstorm Warning"). One SQL bbox prefilter over all queried
/// locations, then a per-location point-in-polygon test in JS against the
/// alert's GeoJSON geometry (bbox containment for zone-based alerts that
/// carry no polygon).
///

import { Database } from "@/earthbrowser/database/Database"
import { type AlertProperties, type AlertSeverity } from "./AlertsTable"

export interface AlertHit {
	alert: AlertProperties
	/** ids of the queried locations inside this alert's area */
	locationIds: string[]
}

export const cSeverityRank: Record<AlertSeverity, number> = {
	Extreme: 0,
	Severe: 1,
	Moderate: 2,
	Minor: 3,
	Unknown: 4,
}

interface QueryLocation {
	id: string
	lat: number
	lon: number
}

type Ring = Array<[number, number]>

/** Ray-cast test of one polygon ring (lon/lat pairs, GeoJSON order) */
function ringContains(ring: Ring, lat: number, lon: number): boolean {
	let inside = false
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i]
		const [xj, yj] = ring[j]
		if ((yi > lat) !== (yj > lat) &&
			lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
			inside = !inside
		}
	}
	return inside
}

/** Point in polygon coordinates (outer ring minus holes) */
function polygonContains(rings: Ring[], lat: number, lon: number): boolean {
	if (rings.length === 0 || !ringContains(rings[0], lat, lon)) return false
	for (let i = 1; i < rings.length; i++) {
		if (ringContains(rings[i], lat, lon)) return false
	}
	return true
}

/** Parse an alert's GeoJSON geometry into polygon ring sets, or null */
function parseGeometry(geometryJson: string): Ring[][] | null {
	try {
		const geometry = JSON.parse(geometryJson) as { type?: string; coordinates?: unknown }
		if (geometry.type === "Polygon") {
			return [geometry.coordinates as Ring[]]
		}
		if (geometry.type === "MultiPolygon") {
			return geometry.coordinates as Ring[][]
		}
	} catch {
		// Fall through to bbox matching
	}
	return null
}

function alertContainsPoint(alert: AlertProperties, polygons: Ring[][] | null, lat: number, lon: number): boolean {
	// bbox test first: cheap, and the only test for zone-based alerts
	if (alert.bbox_min_lat === null || alert.bbox_max_lat === null ||
		alert.bbox_min_lon === null || alert.bbox_max_lon === null) return false
	if (lat < alert.bbox_min_lat || lat > alert.bbox_max_lat ||
		lon < alert.bbox_min_lon || lon > alert.bbox_max_lon) return false

	if (!polygons) return true
	return polygons.some(rings => polygonContains(rings, lat, lon))
}

/**
 * Active alerts covering any of the given locations, most severe first.
 * Reads the local weather_alerts table only - no network. Returns [] when
 * the alerts dataset hasn't synced (or another tab owns the database).
 */
export async function getActiveAlertsForLocations(locations: QueryLocation[]): Promise<AlertHit[]> {
	if (locations.length === 0) return []

	let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180
	for (const location of locations) {
		if (location.lat < minLat) minLat = location.lat
		if (location.lat > maxLat) maxLat = location.lat
		if (location.lon < minLon) minLon = location.lon
		if (location.lon > maxLon) maxLon = location.lon
	}

	const now = new Date().toISOString()
	const candidates = await Database.db.exec(
		`select * from weather_alerts
		where bbox_min_lat is not null
			and bbox_min_lat <= ? and bbox_max_lat >= ?
			and bbox_min_lon <= ? and bbox_max_lon >= ?
			and coalesce(ends, expires, ?) >= ?`,
		[maxLat, minLat, maxLon, minLon, now, now],
		"low"
	) as AlertProperties[]

	const hits: AlertHit[] = []
	for (const alert of candidates) {
		const polygons = alert.geometry ? parseGeometry(alert.geometry) : null
		const locationIds = locations
			.filter(location => alertContainsPoint(alert, polygons, location.lat, location.lon))
			.map(location => location.id)
		if (locationIds.length > 0) {
			hits.push({ alert, locationIds })
		}
	}

	hits.sort((a, b) =>
		(cSeverityRank[a.alert.severity] ?? 4) - (cSeverityRank[b.alert.severity] ?? 4) ||
		b.alert.sent.localeCompare(a.alert.sent))
	return hits
}
